/**
 * Navigation, grouped the way the work is grouped.
 *
 * A flat list of nine is a list you read every time; five short groups is a
 * shape you learn once. The headings name what a person is doing rather than
 * which table they are opening, which is also how the client's own vocabulary
 * runs: they do training, they maintain libraries, they answer to auditors.
 *
 * It lives here rather than in `routes\__root.tsx` because a TanStack route
 * file may export nothing but its `Route` - the plugin's code splitting owns
 * the module - so anything in one is untestable by construction. The rule this
 * carries is worth a test: an Administrator-only screen offered to a
 * Coordinator is a link that can only answer 403.
 */

import {
  Building2,
  CalendarDays,
  ClipboardList,
  History,
  LayoutDashboard,
  ListChecks,
  FileSpreadsheet,
  MessageSquareText,
  ShieldCheck,
  TimerReset,
  Upload,
  Users,
} from "lucide-react"

export type NavItem = {
  to: string
  label: string
  icon: typeof LayoutDashboard
  /** Offered to Administrators, superusers included: the server's own rule. */
  adminOnly?: boolean
  /**
   * Offered to superusers alone, with Administrators inside the refusal.
   *
   * A stricter gate rather than a variant of `adminOnly`, and the one screen
   * that needs it says why: the trail records every act by every role,
   * Administrators included, which is precisely why no role may read the whole
   * of it (R4). A log its own subjects can curate is not evidence.
   */
  superuserOnly?: boolean
}

/**
 * Who is signed in, as much of it as the navigation needs.
 *
 * An object rather than two positional booleans because the two are not
 * independent - every superuser is an Administrator here, mirroring
 * `require_role` letting a superuser past every check - so a transposed pair
 * would read as the coherent-looking nonsense of a superuser who is not an
 * Administrator, and would hide half the navigation without failing anything.
 */
export type NavAudience = { isAdministrator: boolean; isSuperuser: boolean }

export type NavGroup = { label?: string; items: NavItem[] }

export const NAV: NavGroup[] = [
  {
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Training",
    items: [
      { to: "/events", label: "Events", icon: CalendarDays },
      { to: "/records", label: "Records", icon: ClipboardList },
    ],
  },
  {
    label: "Workforce",
    items: [{ to: "/employees", label: "Employees", icon: Users }],
  },
  {
    label: "Libraries",
    // The OFO register is deliberately not here (Dawid, 2026-07-31): it is a
    // background source the system validates codes against, not a database
    // anyone works in. Its screens stay reachable by URL and its API stays,
    // because employee forms and the LMS credential read it.
    items: [
      { to: "/interventions", label: "Interventions", icon: ListChecks },
      { to: "/providers", label: "Providers", icon: Building2 },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/expiring", label: "Expiring training", icon: TimerReset },
      { to: "/reports", label: "Reports", icon: FileSpreadsheet },
    ],
  },
  {
    label: "Data",
    // Administrator-only on the server, every endpoint of both. Offering
    // either link to a Coordinator offers a screen that can only answer 403.
    //
    // Feedback is the asymmetric one and the asymmetry is the design (J4):
    // anybody signed in may SUBMIT, from the button in the chrome, and only an
    // Administrator may READ what was submitted. An entry is a named colleague
    // saying what confused them, which is not something the instance browses.
    //
    // Users and Audit joined it with items 38 and 22. Audit is the one screen
    // in the product gated tighter than Administrator: R4 puts the trail behind
    // `is_superuser`, so the link is offered to a superuser and to nobody else.
    //
    // The screen still has a second door. `GET /api/audit/records` serves an
    // Administrator the sign-off, unlock and attendance slice that is their own
    // defensibility evidence (Q-C, ruled 2026-08-05), and `/audit` renders it
    // for them - by URL only, while this entry stays superuser-only. If that
    // slice is meant to be findable rather than merely reachable, the change is
    // this one word: `superuserOnly` becomes `adminOnly`. Flagged for Dawid
    // rather than decided here, because R4 is a ruling and this is not.
    //
    // Never "OKCA": R4 is explicit, and the product is white-labelled by design.
    items: [
      { to: "/imports", label: "Imports", icon: Upload, adminOnly: true },
      { to: "/users", label: "Users", icon: ShieldCheck, adminOnly: true },
      { to: "/audit", label: "Audit", icon: History, superuserOnly: true },
      { to: "/feedback", label: "Feedback", icon: MessageSquareText, adminOnly: true },
    ],
  },
]

/**
 * The groups this person may be offered, with any group thereby emptied
 * removed rather than left as a heading with nothing under it.
 *
 * A courtesy, never a control: the server refuses regardless, and every screen
 * stays reachable by URL.
 *
 * The two gates are ANDed rather than ordered, so an item carrying both is
 * refused by whichever is stricter and no future combination has a surprising
 * answer.
 */
export function visibleNavGroups({
  isAdministrator,
  isSuperuser,
}: NavAudience): NavGroup[] {
  return NAV.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) =>
        (!item.adminOnly || isAdministrator) && (!item.superuserOnly || isSuperuser),
    ),
  })).filter((group) => group.items.length > 0)
}
